import React, { useState } from 'react';
import { X, Check } from 'lucide-react';
import { formatDuration } from '../utils/units';

type StopDwellPickerModalProps = {
  stopLetter: string;
  initialDwellSec: number;
  arrivalMs: number;
  onConfirm: (dwellSec: number) => void;
  onClose: () => void;
};

const PRESET_DWELLS = [
  { label: 'No stop (0 min)', sec: 0 },
  { label: '15 minutes', sec: 15 * 60 },
  { label: '30 minutes', sec: 30 * 60 },
  { label: '45 minutes', sec: 45 * 60 },
  { label: '1 hour', sec: 3600 },
  { label: '2 hours', sec: 2 * 3600 },
  { label: '4 hours', sec: 4 * 3600 },
  { label: '8 hours', sec: 8 * 3600 },
  { label: '24 hours', sec: 24 * 3600 },
];

export const StopDwellPickerModal: React.FC<StopDwellPickerModalProps> = ({
  stopLetter,
  initialDwellSec,
  arrivalMs,
  onConfirm,
  onClose,
}) => {
  const [tab, setTab] = useState<'for' | 'until'>('for');
  const [selectedDwellSec, setSelectedDwellSec] = useState(initialDwellSec);

  // Time picker state for "until" tab
  const arrivalDate = new Date(arrivalMs || Date.now());
  const initialLeaveDate = new Date((arrivalMs || Date.now()) + initialDwellSec * 1000);
  const [untilTimeString, setUntilTimeString] = useState(
    `${String(initialLeaveDate.getHours()).padStart(2, '0')}:${String(initialLeaveDate.getMinutes()).padStart(2, '0')}`,
  );

  const handleUntilChange = (timeStr: string) => {
    setUntilTimeString(timeStr);
    const [h, m] = timeStr.split(':').map(Number);
    if (h !== undefined && m !== undefined) {
      const leave = new Date(arrivalDate);
      leave.setHours(h, m, 0, 0);
      if (leave.getTime() <= arrivalDate.getTime()) {
        leave.setDate(leave.getDate() + 1); // roll over to next day
      }
      const dwell = Math.max(0, Math.min(86400, Math.round((leave.getTime() - arrivalDate.getTime()) / 1000)));
      setSelectedDwellSec(dwell);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 950,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="sheet-surface"
        style={{
          width: '100%',
          maxWidth: 480,
          borderTopLeftRadius: 36,
          borderTopRightRadius: 36,
          padding: '16px 20px 32px',
          boxSizing: 'border-box',
          color: 'var(--label)',
        }}
      >
        {/* Grabber */}
        <div
          style={{
            width: 36,
            height: 5,
            borderRadius: 2.5,
            backgroundColor: 'var(--label3)',
            margin: '0 auto 16px',
          }}
        />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div className="t-title2">Stop {stopLetter} Dwell Time</div>
            <div className="t-foot c2">How long will you stop here?</div>
          </div>
          <button className="cbtn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Tabs: "Stop for" vs "Stop until" */}
        <div className="seg-control" style={{ marginBottom: 16 }}>
          <button
            className={`seg-item ${tab === 'for' ? 'active' : ''}`}
            onClick={() => setTab('for')}
          >
            Stop for duration
          </button>
          <button
            className={`seg-item ${tab === 'until' ? 'active' : ''}`}
            onClick={() => setTab('until')}
          >
            Stop until time
          </button>
        </div>

        {/* Tab 1: Preset Durations */}
        {tab === 'for' ? (
          <div className="cell-group" style={{ maxHeight: 260, overflowY: 'auto', marginBottom: 20 }}>
            {PRESET_DWELLS.map((preset) => {
              const isSelected = selectedDwellSec === preset.sec;
              return (
                <div
                  key={preset.sec}
                  className="cell"
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  onClick={() => setSelectedDwellSec(preset.sec)}
                >
                  <span className="t-body" style={{ fontWeight: isSelected ? 600 : 400 }}>
                    {preset.label}
                  </span>
                  {isSelected && <Check size={18} color="var(--accent)" />}
                </div>
              );
            })}
          </div>
        ) : (
          /* Tab 2: Stop Until Time */
          <div className="card-surface" style={{ padding: 20, marginBottom: 20 }}>
            <div className="t-foot c2" style={{ marginBottom: 8 }}>
              Estimated arrival: {arrivalDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </div>
            <label className="t-headline" style={{ display: 'block', marginBottom: 8 }}>
              Depart at:
            </label>
            <input
              type="time"
              value={untilTimeString}
              onChange={(e) => handleUntilChange(e.target.value)}
              style={{
                width: '100%',
                height: 44,
                borderRadius: 10,
                border: '1px solid var(--sep)',
                backgroundColor: 'var(--fill3)',
                color: 'var(--label)',
                fontSize: 18,
                padding: '0 12px',
                boxSizing: 'border-box',
              }}
            />
            <div className="t-foot c2" style={{ marginTop: 12 }}>
              Dwell time: <b style={{ color: 'var(--accent)' }}>{formatDuration(selectedDwellSec)}</b>
            </div>
          </div>
        )}

        {/* Confirm Buttons */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn-gray" style={{ flex: 1 }} onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-fill"
            style={{ flex: 2 }}
            onClick={() => {
              onConfirm(selectedDwellSec);
              onClose();
            }}
          >
            Apply ({formatDuration(selectedDwellSec)})
          </button>
        </div>
      </div>
    </div>
  );
};
